import { useApiClient } from '@commander/api-client';
import { Avatar, AvatarFallback, AvatarImage, Badge } from '@commander/ui';
import { useUserDetails } from '@commander/user-details';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { RecommendedIcon } from '@/assets/icons/AuthIcon';
import { createOnboardingService } from '../../services/onboarding.service';
import type { StepProps } from '../../types/onboarding.types';
import ContactLogo from '../shared/ContactLogo';
import PercentageLoader from '../shared/PercentageLoader';

interface CompanyContact {
  id: string;
  company_id: string | null;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  title: string;
  priority_order: number;
  basic_company_id: string;
  country_code: string | null;
  profile_type: string;
  profile_id: string | null;
  created_at: string;
  updated_at: string;
  twitter_handle: string | null;
  instagram_handle: string | null;
  user_id: string | null;
  photo_url: string;
  company_contact_settings: any[];
  has_received_email: boolean;
  has_received_sms: boolean;
}

interface BasicCompany {
  id: string;
  name: string;
  description: string;
  photo_url: string;
  address: string;
  lat: string;
  lng: string;
  website: string;
  short_name: string;
  domain: string | null;
  processed_logo: string | null;
  revenue_range: string;
  profile_id: string | null;
  profile_type: string | null;
  employeeRange: string;
  facebook_url: string | null;
  linkedin_url: string | null;
  x_url: string | null;
  instagram_url: string | null;
  provider_type: string;
  provider_id: string;
  news_events: any[];
  company_contacts: CompanyContact[];
}

interface ICPMatchScore {
  id: string;
  company_prospect_id: string;
  company_id: string;
  company_prospecting_log_id: string | null;
  company_icp_id: string | null;
  active: boolean;
  icp_score: {
    icp_params: Array<{
      icp_param: string;
      icp_score: number;
      icp_value: string | string[];
    }>;
    total_similarity: number;
  };
  raw_response: any;
  validation_response: any;
  review_needed: boolean;
  review_status: string;
  created_at: string;
  updated_at: string;
}

// Simplified interface matching API response
interface LeadData {
  id: string;
  basic_company_id: string;
  company_id: string;
  display_name?: string;
  prospect_status: string;
  source_type: string;
  source_id: string | null;
  company_prospect_upload_id: string | null;
  company_employee_id: string;
  heatmap_thread_id: string | null;
  engaged_at: string | null;
  closed_at: string | null;
  customer_at: string | null;
  rank: number | null;
  deleted_at: string | null;
  targeting_at: string | null;
  unread_notifications_count: number;
  approved: boolean;
  approved_by: string;
  approved_at: string;
  from_heatmap: boolean;
  cold_lead: boolean;
  notes: string | null;
  denied_at: string | null;
  denied_by: string | null;
  annual_revenue: number | null;
  prospect_stage: string;
  customer_quoted_at: string | null;
  prospecting_status: string;
  manual_prospect: boolean;
  email_style_notes: string | null;
  good_fit_reason: string | null;
  last_llm_scored_at: string | null;
  auto_approval_status: string | null;
  active_prospect_approval_score_id: string | null;
  contractor_record_id: string | null;
  processed: boolean;
  processed_at: string | null;
  archived_at: string | null;
  company_prospect_upload_row_id: string | null;
  decision_maker_viewed: boolean;
  has_epa_signal: boolean;
  epa_signal_score: number | null;
  created_at: string;
  updated_at: string;
  company: any;
  icp_match_scores: ICPMatchScore[];
  sales_strategy: any;
  prospect_tags: any[];
  company_prospect_feedbacks: any[];
  basic_company: BasicCompany;
  contractorRecord: any;
}

const PreviewLeads: React.FC<StepProps> = () => {
  // API hooks
  const apiClient = useApiClient();
  const { userDetails } = useUserDetails();
  const onboardingService = useMemo(() => createOnboardingService(apiClient), [apiClient]);

  // State management
  const [leads, setLeads] = useState<LeadData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch prospects data when dependencies change
  useEffect(() => {
    const fetchProspects = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Get company ID from user details or fallback to localStorage
        const getCompanyId = () => {
          if (userDetails?.company?.id) {
            return userDetails.company.id;
          }

          // Fallback to localStorage with defensive JSON parsing
          const userDetailsString = localStorage.getItem('user_details');
          if (!userDetailsString) {
            return '';
          }

          try {
            const user = JSON.parse(userDetailsString);
            return user.company_employee?.company?.id || '';
          } catch (parseError) {
            console.warn('Failed to parse user details from localStorage:', parseError);
            return '';
          }
        };

        const companyId = getCompanyId();
        if (!companyId) {
          setError('Company ID not found. Please complete company information first.');
          return;
        }

        const response = await onboardingService.previewProspects(companyId);

        if (response.success && response.data?.recommendedProspects) {
          // Use prospects data directly
          setLeads(response.data.recommendedProspects);
        } else {
          setError(response.message || 'Failed to fetch prospects data');
        }
      } catch (err) {
        console.error('Error fetching prospects:', err);
        setError('Failed to load prospects. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProspects();
  }, [onboardingService, userDetails]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading prospects...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="text-red-600 mb-2">⚠️ {error}</div>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="text-blue-600 hover:text-blue-800 underline"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Empty state
  if (leads.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center text-gray-500">
          <div className="mb-2">📋</div>
          <div>No prospects found</div>
          <div className="text-sm mt-1">Complete your onboarding to see recommended prospects</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-9">
        {leads.slice(0, 2).map((lead) => (
          <div key={lead.id} className="rounded-xl h-full">
            {/* Lead Header */}
            <div
              className={`flex items-start gap-2 p-4 border border-gray-200 rounded-t-xl transition-all duration-200 ${lead?.basic_company?.company_contacts?.length > 0 ? 'rounded-b-none' : 'rounded-b-xl border-b'}`}
            >
              <div className="relative flex gap-4 items-start flex-1 min-w-0">
                <div className="flex items-start flex-shrink-0">
                  <Avatar size="sm" shape="square" className="w-[67px] h-[50px] flex-shrink-0">
                    <AvatarImage
                      src={lead.basic_company?.photo_url}
                      alt={lead.display_name || lead.basic_company?.name || 'CO'}
                      className="object-contain p-0.5"
                    />
                    <AvatarFallback className="bg-primary/10 text-primary font-semibold text-xs">
                      {(lead.display_name || lead.basic_company?.name || 'CO')
                        .split(' ')
                        .map((n) => n[0])
                        .join('')
                        .toUpperCase()
                        .slice(0, 2)}
                    </AvatarFallback>
                  </Avatar>
                </div>
                <div className="flex flex-col gap-y-1.5 flex-1 min-w-0 overflow-hidden">
                  <div>
                    <Badge
                      variant="secondary"
                      className="max-w-[201px] bg-[#F9E3FF] text-[#4C1A5A] text-xs px-2 py-0.5"
                    >
                      <span>Recommended</span>
                      <RecommendedIcon />
                    </Badge>
                  </div>
                  <div className="flex items-center space-x-2 min-w-0">
                    <span className="text-lg font-medium text-[#2587BD] whitespace-normal break-words">
                      {lead?.display_name || lead?.basic_company?.name}
                    </span>
                  </div>

                  {lead?.icp_match_scores?.length > 0 && (
                    <div className="text-xs text-gray-600 min-w-0">
                      <span className="whitespace-normal break-words">
                        {lead.icp_match_scores[0]?.icp_score?.icp_params?.[0]?.icp_param
                          ?.charAt(0)
                          ?.toUpperCase() +
                          (
                            lead.icp_match_scores[0]?.icp_score?.icp_params?.[0]?.icp_param || ''
                          ).slice(1)}
                        :{' '}
                        <span className="whitespace-normal break-words">
                          {Array.isArray(
                            lead.icp_match_scores[0]?.icp_score?.icp_params?.[0]?.icp_value
                          )
                            ? lead.icp_match_scores[0]?.icp_score?.icp_params?.[0]?.icp_value.join(
                                ', '
                              )
                            : lead.icp_match_scores[0]?.icp_score?.icp_params?.[0]?.icp_value}
                        </span>
                      </span>
                    </div>
                  )}
                  {lead?.icp_match_scores?.length > 0 && (
                    <div className="flex gap-1.5 items-center text-gray-600">
                      <PercentageLoader
                        percentage={lead?.icp_match_scores?.[0]?.icp_score?.total_similarity || 0}
                        size={18}
                        page="prospectList"
                        strokeWidth={3}
                      />
                      <span className="text-sm text-gray-500">
                        {lead?.icp_match_scores?.[0]?.icp_score?.total_similarity}% Match
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {lead?.basic_company?.company_contacts?.length > 0 && (
              <div className="">
                <div className="text-sm font-medium px-2.5 pt-2.5 border-r border-l border-gray-200 border-b">
                  Generated Contacts
                </div>
                {(() => {
                  const contacts = lead?.basic_company?.company_contacts?.slice(0, 2);
                  return contacts.map((contact, index) => (
                    <div
                      key={contact.id ?? index}
                      className={`flex items-start w-full p-2 border-r border-l border-[#E1E7EC] gap-2 ${index === contacts.length - 1 ? 'rounded-b-xl border-b' : 'border-b'}`}
                    >
                      <div className="flex gap-3 w-full p-2 bg-[#F7FAFA] rounded-md">
                        <div>
                          <ContactLogo
                            photoUrl={contact?.photo_url ?? ''}
                            firstName={contact?.first_name ?? ''}
                            lastName={contact?.last_name ?? ''}
                          />
                        </div>
                        <div className="flex items-center sm:flex-nowrap h-full flex-wrap gap-1 justify-between w-full">
                          <div className="flex flex-col max-w-[277px] w-full">
                            <div className="relative group flex w-full">
                              <span
                                className="text-xs font-medium text-black w-full max-w-[130px] sm:max-w-[245px] truncate"
                                ref={(el) => {
                                  if (el && el.offsetWidth < el.scrollWidth) {
                                    el.setAttribute('data-truncated', 'true');
                                  } else {
                                    el?.removeAttribute('data-truncated');
                                  }
                                }}
                              >
                                {contact?.first_name || ''} {contact?.last_name || ''}
                              </span>
                            </div>
                            <div className="flex gap-1 items-center flex-wrap sm:flex-nowrap">
                              <div className="relative group flex ">
                                <span
                                  className="text-[#454A51] text-xs font-medium w-full max-w-[130px] sm:max-w-[245px] md:max-w-[277px] truncate tracking-[0.1px]"
                                  ref={(el) => {
                                    if (el && el.offsetWidth < el.scrollWidth) {
                                      el.setAttribute('data-truncated', 'true');
                                    } else {
                                      el?.removeAttribute('data-truncated');
                                    }
                                  }}
                                >
                                  {contact?.title}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PreviewLeads;
